import { z } from "zod";

export const QaEnrollmentBaselineResetRecipeIdSchema = z.literal(
  "restore_enrollment_contract",
);

export const QaEnrollmentContractBaselineStateSchema = z.enum([
  "unsigned",
  "signed",
]);

export const QaEnrollmentContractObservedStateSchema = z.enum([
  "unavailable",
  "unsigned",
  "signed",
  "inconsistent",
]);

export const QaEnrollmentSignedSnapshotSourceSchema = z.enum([
  "live",
  "captured",
  "none",
]);

export const QaEnrollmentBaselineBlockerSchema = z
  .object({
    code: z.enum([
      "missing_contract_configuration",
      "missing_contract_version",
      "missing_eligible_children",
      "missing_signed_snapshot",
      "ambiguous_signed_snapshot",
      "contract_identity_drift",
    ]),
    detail: z.string().trim().min(1),
  })
  .strict();

export const QaEnrollmentLifecycleDeltaValueSchema = z.discriminatedUnion(
  "state",
  [
    z.object({ state: z.literal("missing") }).strict(),
    z.object({ state: z.literal("null") }).strict(),
    z
      .object({
        state: z.literal("value"),
        value: z.union([z.string(), z.number(), z.boolean()]),
      })
      .strict(),
    z
      .object({
        state: z.literal("redacted"),
        present: z.boolean(),
      })
      .strict(),
  ],
);

export const QaEnrollmentLifecycleDeltaEntrySchema = z
  .object({
    scope: z.enum([
      "application",
      "configuration",
      "contract_version",
      "child",
    ]),
    recordId: z.string().trim().min(1),
    field: z.string().trim().min(1),
    before: QaEnrollmentLifecycleDeltaValueSchema,
    after: QaEnrollmentLifecycleDeltaValueSchema,
  })
  .strict();

export const QaEnrollmentBaselineResetPreviewInputSchema = z
  .object({
    applicantId: z.string().uuid(),
    familyId: z.string().uuid(),
    academicYear: z.string().regex(/^\d{4}-\d{4}$/),
    targetState: QaEnrollmentContractBaselineStateSchema,
  })
  .strict();

export const QaEnrollmentBaselineResetApplyInputSchema =
  QaEnrollmentBaselineResetPreviewInputSchema.extend({
    actorId: z.string().trim().min(1),
    previewOperationId: z.string().uuid(),
    expectedBeforeStateDigest: z.string().length(64),
    confirmationText: z.string().trim().min(1),
    idempotencyKey: z
      .string()
      .min(8)
      .max(180)
      .regex(/^[a-zA-Z0-9._:-]+$/),
  }).strict();

export const QaEnrollmentBaselineResetRollbackInputSchema = z
  .object({
    applicantId: z.string().uuid(),
    familyId: z.string().uuid(),
    academicYear: z.string().regex(/^\d{4}-\d{4}$/),
    targetState: QaEnrollmentContractBaselineStateSchema,
    actorId: z.string().trim().min(1),
    rollbackToken: z.string().uuid(),
    expectedStateDigest: z.string().length(64),
    confirmationText: z.string().trim().min(1),
  })
  .strict();

export const QaEnrollmentBaselineResetPreviewSchema = z
  .object({
    recipeId: QaEnrollmentBaselineResetRecipeIdSchema,
    previewOperationId: z.string().uuid(),
    applicationId: z.string().uuid(),
    applicantId: z.string().uuid(),
    familyId: z.string().uuid(),
    familyName: z.string().trim().min(1),
    academicYear: z.string().regex(/^\d{4}-\d{4}$/),
    contractVersion: z.string().trim().min(1).nullable(),
    currentState: QaEnrollmentContractObservedStateSchema,
    targetState: QaEnrollmentContractBaselineStateSchema,
    signedSnapshotSource: QaEnrollmentSignedSnapshotSourceSchema,
    eligibleChildApplicationIds: z.array(z.string().uuid()),
    affectedRecordCount: z.number().int().nonnegative(),
    affectedChildCount: z.number().int().nonnegative(),
    preservedDocumentCount: z.number().int().nonnegative(),
    preservedRegistrationPaymentCount: z.number().int().nonnegative(),
    lifecycleDelta: z.array(QaEnrollmentLifecycleDeltaEntrySchema),
    blockers: z.array(QaEnrollmentBaselineBlockerSchema),
    canApply: z.boolean(),
    beforeStateDigest: z.string().length(64),
    targetStateDigest: z.string().length(64),
    confirmationText: z.string().trim().min(1),
  })
  .strict();

export const QaEnrollmentBaselineResetApplyResultSchema = z
  .object({
    status: z.enum(["applied", "already_applied", "no_change"]),
    preview: QaEnrollmentBaselineResetPreviewSchema,
    applicationId: z.string().uuid(),
    verifiedState: QaEnrollmentContractBaselineStateSchema,
    verifiedChildCount: z.number().int().nonnegative(),
    afterStateDigest: z.string().length(64),
    rollbackToken: z.string().uuid().nullable(),
    rollbackConfirmationText: z.string().trim().min(1).nullable(),
    idempotencyKey: z.string().trim().min(1),
    appliedAt: z.string().datetime(),
  })
  .strict();

export const QaEnrollmentBaselineResetRollbackResultSchema = z
  .object({
    status: z.enum(["rolled_back", "already_rolled_back"]),
    applicationId: z.string().uuid(),
    restoredState: QaEnrollmentContractObservedStateSchema,
    afterStateDigest: z.string().length(64),
    rolledBackAt: z.string().datetime(),
  })
  .strict();

export const buildQaEnrollmentBaselineResetConfirmationText = (input: {
  familyName: string;
  academicYear: string;
  targetState: z.infer<typeof QaEnrollmentContractBaselineStateSchema>;
}): string =>
  `RESTORE ${input.familyName.trim().toUpperCase()} ${input.academicYear} ENROLLMENT CONTRACT TO ${input.targetState.toUpperCase()}`;

export const buildQaEnrollmentBaselineResetRollbackConfirmationText = (input: {
  familyName: string;
  academicYear: string;
}): string =>
  `ROLL BACK ENROLLMENT CONTRACT FOR ${input.familyName.trim().toUpperCase()} — ${input.academicYear}`;

export type QaEnrollmentContractBaselineState = z.infer<
  typeof QaEnrollmentContractBaselineStateSchema
>;
export type QaEnrollmentContractObservedState = z.infer<
  typeof QaEnrollmentContractObservedStateSchema
>;
export type QaEnrollmentBaselineResetPreviewInput = z.infer<
  typeof QaEnrollmentBaselineResetPreviewInputSchema
>;
export type QaEnrollmentLifecycleDeltaValue = z.infer<
  typeof QaEnrollmentLifecycleDeltaValueSchema
>;
export type QaEnrollmentLifecycleDeltaEntry = z.infer<
  typeof QaEnrollmentLifecycleDeltaEntrySchema
>;
export type QaEnrollmentBaselineResetApplyInput = z.infer<
  typeof QaEnrollmentBaselineResetApplyInputSchema
>;
export type QaEnrollmentBaselineResetRollbackInput = z.infer<
  typeof QaEnrollmentBaselineResetRollbackInputSchema
>;
export type QaEnrollmentBaselineResetPreview = z.infer<
  typeof QaEnrollmentBaselineResetPreviewSchema
>;
export type QaEnrollmentBaselineResetApplyResult = z.infer<
  typeof QaEnrollmentBaselineResetApplyResultSchema
>;
export type QaEnrollmentBaselineResetRollbackResult = z.infer<
  typeof QaEnrollmentBaselineResetRollbackResultSchema
>;
